import React, { ReactNode } from 'react';

const helpText: Record<string, { header: string; body: ReactNode }> = {
  dba: {
    header: 'Doing Business As (DBA) Name',
    body: (
      <>
        <p>
          The Doing Business As (DBA) Name is the name under which a company operates. The DBA is visible to the public,
          but is not the legal, registered name of that organization.
        </p>
      </>
    ),
  },
  duns: {
    header: 'DUNS Number',
    body: (
      <>
        <p>
          A DUNS number is a unique, 9-digit identifier issued and maintained by Dun & Bradstreet that can help verify
          the existence of a business entity.{' '}
          <a
            href="https://www.dnb.com/duns-number.html"
            rel="noopener noreferrer"
            target="_blank"
            aria-label="Learn more about the DUNS Number in a new tab"
          >
            Learn more about the DUNS Number.
          </a>
        </p>
      </>
    ),
  },
  employerIdentification: {
    header: 'Employer Identification Number (EIN)',
    body: (
      <>
        <p>
          An Employer Identification Number (EIN) is also known as the Federal Tax Identification Number, and is used to
          identify a business entity. An EIN is required for an employer to enroll in E-Verify. Employers who do not
          have an EIN can{' '}
          <a
            href="https://www.irs.gov/businesses/small-businesses-self-employed/apply-for-an-employer-identification-number-ein-online"
            rel="noopener noreferrer"
            target="_blank"
            aria-label="Apply for an EIN in a new tab"
          >
            apply online
          </a>{' '}
          with the IRS to receive an EIN immediately.
        </p>
      </>
    ),
  },
  naicsCode: {
    header: 'NAICS Code',
    body: (
      <>
        <p>
          The North American Industry Classification System (NAICS) classifies businesses by industry to collect,
          analyze, and publish statistical data related to the U.S. business economy. A three (3) digit NAICS code is
          required for an employer to enroll in E-Verify. If you do not know your client company&apos;s three (3) digit
          NAICS code, you can select the sector and subsector of the client company to determine the NAICS code.
        </p>
      </>
    ),
  },
  uei: {
    header: 'Unique Entity Identifier (UEI)',
    body: (
      <>
        <p>
          A UEI is a 12-digit alphanumeric identifier that is provided by SAM.gov to all entities who register to do
          business with the federal government.{' '}
          <a
            href="https://sam.gov/content/duns-uei"
            rel="noopener noreferrer"
            target="_blank"
            aria-label="Learn more about the UEI in a new tab"
          >
            Learn more about the UEI.
          </a>
        </p>
      </>
    ),
  },
  error: {
    header: 'Error',
    body: (
      <>
        <p>Hmmmm... There seems to be an issue with your selection.</p>
      </>
    ),
  },
  government: {
    header: 'Government',
    body: (
      <>
        <p>Select this category if your organization is part of:</p>
        <ul>
          <li>The U.S. federal government</li>
          <li>A U.S. state or territorial government</li>
          <li>A U.S. local government</li>
        </ul>
      </>
    ),
  },
  federalContractor: {
    header: 'Federal Contractor',
    body: (
      <>
        <p>Select this option if your company contracts with the U.S. federal government.</p>
      </>
    ),
  },
  airportOperator: {
    header: 'Airport Operator',
    body: (
      <>
        <p>
          Select this category if your company is an Airport Operator that oversees an airport that serves an aircraft
          operator or a foreign air carrier and is required by TSA to have a security program. Airport Operators use
          E-Verify to confirm the identity and employment eligibility of applicants to help determine eligibility for
          unescorted access to a Security Identification Display Area (SIDA) of an airport. If you select Airport
          Operator, your enrollment information will be verified with the Transportation Security Administration (TSA).
        </p>
      </>
    ),
  },
  noneOfTheAbove: {
    header: 'None of these categories apply',
    body: (
      <>
        <p>Select this category if your company is not a federal contractor, government entity, or airport operator.</p>
      </>
    ),
  },
  federalContractorWithFarEverifyClause: {
    header: 'Federal Contractor with FAR E-Verify Clause',
    body: (
      <>
        <p>
          Select this category if your organization contracts with the U.S. federal government and your organization HAS
          been awarded a contract that contains the FAR E-Verify clause. Also select this category if your organization
          is a subcontractor on any contract that contains the FAR E-Verify clause.
        </p>
      </>
    ),
  },
  federalContractorWithoutFarEverifyClause: {
    header: 'Federal Contractor without FAR E-Verify Clause',
    body: (
      <>
        <p>
          Select this category if your organization contracts with the U.S. federal government, but HAS NOT been awarded
          a contract that contains the FAR E-Verify clause. Also select this category if your organization is a
          subcontractor on contracts in which none of the contracts contains the FAR E-Verify clause.
        </p>
      </>
    ),
  },
  federalGovernment: {
    header: 'Federal Government',
    body: (
      <>
        <p>
          Select this category if your organization is part of the U.S. federal government. Common federal organizations
          include federal agencies, U.S. government-owned corporations and Congress.
        </p>
      </>
    ),
  },
  stateGovernment: {
    header: 'State Government',
    body: (
      <>
        <p>
          Select this category if your organization is part of a U.S. state or territorial government. Common state
          organizations include state agencies, state-owned enterprises and state legislatures. If your organization is
          part of a state or territorial government that is required to use E-Verify because it has a federal contract
          that contains the FAR E-Verify clause, select ‘Federal Contractor with FAR E-Verify Clause‘ in the next step.
        </p>
      </>
    ),
  },
  localGovernment: {
    header: 'Local Government',
    body: (
      <>
        <p>
          Select this category if your organization is part of a U.S. local government. Common local governments include
          counties and local municipalities, such as cities, towns, and villages. If your organization is part of a U.S.
          local government that is required to use E-Verify because it has a federal contract that contains the FAR
          E-Verify clause, select ‘Federal Contractor with FAR E-Verify Clause‘ in the next step.
        </p>
      </>
    ),
  },
  noneOfTheAboveTier3: {
    header: 'None of these categories apply',
    body: (
      <>
        <p>
          Select this category if your company is not an institution of higher education, state or local government,
          federally recognized Indian tribe, or surety.
        </p>
      </>
    ),
  },
  instituteOfHigherLearning: {
    header: 'Institution of Higher Education',
    body: (
      <>
        <p>
          Select this category if the contractor is an Institution of Higher Education, as defined at 20 U.S.C. Section
          1001(a). The contractor may choose to only verify existing employees and new hires assigned to a covered
          federal contract.
        </p>
      </>
    ),
  },
  stateOrLocalGovernment: {
    header: 'State or Local Government',
    body: (
      <>
        <p>
          Select this category if the contractor is a State or local government entity. The contractor may choose to
          only verify existing employees and new hires assigned to a covered federal contract.
        </p>
      </>
    ),
  },
  federallyRecognizedIndianTribe: {
    header: 'Federally Recognized Indian Tribe',
    body: (
      <>
        <p>
          Select this category if the contractor is a Federally recognized Indian tribe. The contractor may choose only
          to verify existing employees and new hires assigned to a covered federal contract.
        </p>
      </>
    ),
  },
  sureties: {
    header: 'Sureties',
    body: (
      <>
        <p>
          Select this category if the contractor is a surety performing under a takeover agreement entered into with a
          Federal agency pursuant to a performance bond. The contractor may choose to only verify existing employees and
          new hires assigned to a covered federal contract.
        </p>
      </>
    ),
  },
  // the API has this named as NEW_AND_EXISTING_HIRE_ON_FED_CONT. This is confusing as the header seems to indicate it would
  // match the ALL_NEW_AND_EXISTING_FED_CONT_HIRE from the API, but this is most certainly not the case
  newHiresAndAssignedToACoveredFederalContract: {
    header: 'All new hires and all existing employees assigned to a Federal contract',
    body: (
      <>
        <p>
          Select this option if your company will verify all new hires throughout the company and all existing employees
          who are assigned to their covered federal contracts.
        </p>
      </>
    ),
  },
  entireWorkforce: {
    header: 'Entire workforce (all new hires and all existing employees throughout the entire company)',
    body: (
      <>
        <p>
          Select this option if your company will verify all new hires and all existing employees throughout the entire
          company.
        </p>
      </>
    ),
  },
  assignedToACoveredFederalContractOnly: {
    header: 'Employees assigned to a covered Federal contract only',
    body: (
      <>
        <p>
          Select this option if your company fits one of the exceptions (Institution of Higher Education, State or local
          government entity, Federally recognized Indian tribe or surety) and will only verify new hires and existing
          employees assigned to a covered federal contract.
        </p>
      </>
    ),
  },
  // the API has this named as ALL_NEW_AND_EXISTING_FED_CONT_HIRE. This is confusing as a previous header seems to indicate it
  // should match this variable, but it very much does not (see previous comment)
  allNewHiresAndAssignedToACoveredFederalContract: {
    header: 'Employees assigned to a covered Federal contract and new hires throughout the organization',
    body: (
      <>
        <p>
          Select this option if your company will verify all existing employees newly assigned to a covered federal
          contract and all new hires throughout the company.
        </p>
      </>
    ),
  },
  hiringSites: {
    header: 'Hiring Sites',
    body: (
      <>
        <p>
          A hiring site is the location where employees are hired and complete Form I-9. Employers select which hiring
          sites participate in E-Verify and must verify all newly hired employees for that hiring site. If you decide
          not to have a hiring site participate, you are not permitted to verify any employees at that location.
        </p>
      </>
    ),
  },
  programAdministrator: {
    header: 'Program Administrator(s)',
    body: (
      <>
        <p>
          Program Administrators have the ability to create and manage cases and user accounts, run reports, update
          account information, and unlock passwords for other user accounts. A company must have at least one Program
          Administrator.
        </p>
      </>
    ),
  },
  federalContractorsSubjectFAREVerifyClause: {
    header: 'Federal Contractors Subject to the FAR E-Verify Clause',
    body: (
      <>
        <p>
          The E-Verify Federal contractor rule requires that the FAR E-Verify clause be added to applicable federal
          contracts issued on or after September 8, 2009. The clause is a term in the contract in which federal
          contractors commit to use E-Verify to verify that all of their new hires and all employees (existing and new)
          assigned to a federal contract are authorized to work in the United States.
        </p>
      </>
    ),
  },
  mouSignatory: {
    header: 'Signatory',
    body: (
      <>
        <p>
          The Signatory must have authority to sign the Terms and Conditions on behalf of your company. They can also
          optionally be registered as a Program Administrator. A company must have one Signatory.
        </p>
      </>
    ),
  },
  state: {
    header: 'State or U.S. Territory',
    body: (
      <>
        <p>
          The E-Verify statute limits the scope of E-Verify operations to the United States, which includes the 50
          states, the District of Columbia, Puerto Rico, the U.S. Virgin Islands, Guam, and the Commonwealth of the
          Northern Mariana Islands.
        </p>
      </>
    ),
  },
};

export default helpText;
